// libraries
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { useSelector, useDispatch } from 'react-redux'

// generic components
import Tooltip from 'components/generic/tooltip'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import StyledLink from 'components/generic/typography/StyledLink.jsx'

// state
import { setCustomer } from 'state/customer'
import { setLoading } from 'state/loading'

// helpers and utils
import { SORTABLE_IBT_FIELDS, PAGE_SIZE } from 'utils/constants'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const getColumns = (
  useBackendSortAndFilter,
  renderHeaderWithSort,
  defaultYardTimezone = null,
  handleNavigation,
  useMinimalObt,
) => {
  const columns = [
    {
      field: 'ticketId',
      headerName: 'Ticket #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
      renderCell: params => (
        <StyledLink
          to={params?.row?.ticketLink}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            if (event.metaKey) {
              window.open(params?.row?.ticketLink, '_blank')
            } else {
              handleNavigation(params)
            }
          }}
        >
          {params.row.ticketId}
        </StyledLink>
      ),
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Ticket #', SORTABLE_IBT_FIELDS.TICKET_ID, { startAscending: false })
        : undefined,
    },
    {
      field: 'ticketStatus',
      headerName: 'Status',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'ticketEffectiveDate',
      headerName: 'Effective Date',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      sortable: !useBackendSortAndFilter,
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Effective Date', SORTABLE_IBT_FIELDS.EFFECTIVE_DATE, { startAscending: false })
        : undefined,
      renderCell: params => dateTimeFormatter(params.row.ticketEffectiveDate, { timeZone: defaultYardTimezone }),
    },
    {
      field: 'customerIdentifierString',
      headerName: 'Customer',
      flex: 1,
      sortable: !useBackendSortAndFilter,
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Customer', SORTABLE_IBT_FIELDS.CUSTOMER_DISPLAY_NAME)
        : undefined,
    },
    {
      field: 'truckNumber',
      headerName: 'Truck #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'trailerNumber',
      headerName: 'Trailer #',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'billOfLading',
      headerName: 'Bill of Lading',
      flex: 1,
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'materials',
      headerName: 'Material',
      flex: 1,
      sortable: false,
      renderCell: params => {
        const { materialTypeCommonNames } = params.row
        return (
          <Tooltip content={materialTypeCommonNames?.join(', ')}>
            <>{materialTypeCommonNames?.length > 1 ? 'Mixed' : materialTypeCommonNames?.[0] || ''}</>
          </Tooltip>
        )
      },
    },
    {
      field: 'grossWeight',
      headerName: 'Gross Weight',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'tareWeight',
      headerName: 'Tare Weight',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      sortable: !useBackendSortAndFilter,
    },
    {
      field: 'netWeight',
      headerName: 'Net Weight',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      sortable: !useBackendSortAndFilter,
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Net Weight', SORTABLE_IBT_FIELDS.NET_WEIGHT, { align: 'right' })
        : undefined,
    },
    {
      field: 'netValue',
      headerName: 'Total Value',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      sortable: !useBackendSortAndFilter,
      renderHeader: useBackendSortAndFilter
        ? () => renderHeaderWithSort('Total Value', SORTABLE_IBT_FIELDS.TOTAL_VALUE, { align: 'right' })
        : undefined,
      renderCell: ({ row }) =>
        `${row?.pricePrepend || '$'}${setDecimalPrecision(row?.netValue, { decimalPrecisionOverride: 2 })}`,
    },
  ]
  const filteredColumns = columns.filter(column => {
    if (!useMinimalObt && column.field === 'trailerNumber') return false
    if (!useMinimalObt && column.field === 'grossWeight') return false
    if (!useMinimalObt && column.field === 'tareWeight') return false
    if (useMinimalObt && column.field === 'netValue') return false
    return true
  })
  return filteredColumns
}

const InboundTicketsListView = props => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { hideCustomerColumn, filter, setFilter, useBackendSortAndFilter, setQueryOptions } = props
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { useMinimalObt } = useSelector(state => state.featureFlags?.variables || {})

  useEffect(() => {
    setQueryOptions(prevQuery => ({
      ...prevQuery,
      pageSize: PAGE_SIZE,
    }))
  }, [])

  const renderHeaderWithSort = useMemo(() => generateRenderHeaderFunction(filter, setFilter), [filter])

  const handleNavigation = params => {
    const { row } = params
    const { id, customerCommonIdentifierString } = row?.customer || {}
    dispatch(setCustomer({ id, customerCommonIdentifierString, supplierType: '' }))
    dispatch(setLoading(true))
    setTimeout(() => {
      navigate(params?.row?.ticketLink)
    }, 0)
  }

  const tableColumns = getColumns(
    useBackendSortAndFilter,
    renderHeaderWithSort,
    defaultYardTimezone,
    handleNavigation,
    useMinimalObt,
  ).filter(col => col.headerName !== 'Customer' || !hideCustomerColumn)

  return (
    <DataGridWithSearch
      {...props}
      className={classes.root}
      columns={tableColumns}
      onRowClick={(params, event) => {
        if (event.metaKey) {
          window.open(params?.row?.ticketLink, '_blank')
        } else {
          handleNavigation(params)
        }
      }}
    />
  )
}

InboundTicketsListView.propTypes = {
  ...DataGridWithSearch.propTypes,
  hideCustomerColumn: PropTypes.bool,
  setQueryOptions: PropTypes.func,
}

InboundTicketsListView.defaultProps = {
  ...DataGridWithSearch.defaultProps,
  hideCustomerColumn: false,
  setQueryOptions: () => {},
}

export default InboundTicketsListView
